/**
 * Hex-based color palette aligned to DESIGN.md.
 *
 * Brand-critical shades (primary, ai, competitor) are anchored to the exact
 * hex values called out in DESIGN.md. Neutral / status scales mirror the
 * standard Tailwind v3 palette that DESIGN.md references (gray / emerald /
 * amber / red / blue).
 *
 * Seed-based generators (`generateScale`, `generateMinimalScale`) are kept
 * for compatibility with existing Storybook stories that visualize OKLCH
 * ramps. They are no longer the source of truth for the canonical palette.
 *
 * @see ../../DESIGN.md
 */

export type ColorSeed = {
  readonly hue: number
  readonly chroma: number
}

export const SHADE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const
export type ShadeStep = (typeof SHADE_STEPS)[number]
export type ShadeScale = Record<ShadeStep, string>

export const MINIMAL_STEPS = [100, 500, 700, 900] as const
export type MinimalShadeStep = (typeof MINIMAL_STEPS)[number]
export type MinimalShadeScale = Record<MinimalShadeStep, string>

// ── OKLCH seed utilities (back-compat for stories) ───────────

const LIGHTNESS_CURVE: Record<ShadeStep, number> = {
  50: 0.97, 100: 0.93, 200: 0.87, 300: 0.78, 400: 0.68, 500: 0.55,
  600: 0.45, 700: 0.37, 800: 0.29, 900: 0.21, 950: 0.14,
}

const CHROMA_CURVE: Record<ShadeStep, number> = {
  50: 0.12, 100: 0.25, 200: 0.45, 300: 0.70, 400: 0.90, 500: 1.00,
  600: 0.95, 700: 0.85, 800: 0.70, 900: 0.50, 950: 0.35,
}

function oklch(l: number, c: number, h: number): string {
  return `oklch(${round(l)} ${round(c)} ${round(h)})`
}

function round(n: number): string {
  return Number(n.toFixed(4)).toString()
}

export function generateScale(seed: ColorSeed): ShadeScale {
  const result = {} as Record<ShadeStep, string>
  for (const step of SHADE_STEPS) {
    const l = LIGHTNESS_CURVE[step]
    const c = seed.chroma * CHROMA_CURVE[step]
    result[step] = oklch(l, c, seed.hue)
  }
  return result as ShadeScale
}

export function generateMinimalScale(seed: ColorSeed): MinimalShadeScale {
  const full = generateScale(seed)
  return { 100: full[100], 500: full[500], 700: full[700], 900: full[900] }
}

// Kept so Colors.stories.tsx still compiles. Not used by themes.ts.
export const seeds = {
  primary: { hue: 264, chroma: 0.19 } as const satisfies ColorSeed,
  secondary: { hue: 220, chroma: 0.02 } as const satisfies ColorSeed,
  neutral: { hue: 220, chroma: 0.02 } as const satisfies ColorSeed,
  error: { hue: 25, chroma: 0.18 } as const satisfies ColorSeed,
  warning: { hue: 75, chroma: 0.14 } as const satisfies ColorSeed,
  success: { hue: 163, chroma: 0.16 } as const satisfies ColorSeed,
  info: { hue: 245, chroma: 0.14 } as const satisfies ColorSeed,
} as const

export const extendedSeeds = {
  violet: { hue: 300, chroma: 0.16 } as const satisfies ColorSeed,
  orange: { hue: 50, chroma: 0.15 } as const satisfies ColorSeed,
  lime: { hue: 135, chroma: 0.14 } as const satisfies ColorSeed,
} as const

// ── Canonical palette (hex, DESIGN.md-aligned) ───────────────

/**
 * Brand Blue — DESIGN.md `{colors.primary}` = #0B00F3.
 * Hover = #0900c4 (DESIGN.md `{colors.primary-hover}`).
 * Other shades are tuned by hand to provide a coherent 50–950 ramp around
 * the spec values at 600/700.
 */
const primary: ShadeScale = {
  50: "#f0eeff",
  100: "#dcd9ff",
  200: "#b8b3ff",
  300: "#948dff",
  400: "#5e54ff",
  500: "#2a1aff",
  600: "#0B00F3", // DESIGN.md brand main
  700: "#0900c4", // DESIGN.md brand hover
  800: "#07009b",
  900: "#04006d",
  950: "#02003d",
}

/**
 * Secondary — no second brand color in DESIGN.md. Mapped to neutral grays so
 * any current `bg-secondary` consumer renders as a muted surface rather than
 * the legacy cyan. Marked for removal in cleanup.
 */
const secondary: ShadeScale = {
  50: "#f9fafb",
  100: "#f3f4f6",
  200: "#e5e7eb",
  300: "#d1d5db",
  400: "#9ca3af",
  500: "#6b7280",
  600: "#4b5563",
  700: "#374151",
  800: "#1f2937",
  900: "#111827",
  950: "#030712",
}

/** Tailwind gray — DESIGN.md neutral foundation. */
const neutral: ShadeScale = {
  50: "#f9fafb",
  100: "#f3f4f6",
  200: "#e5e7eb",
  300: "#d1d5db",
  400: "#9ca3af",
  500: "#6b7280",
  600: "#4b5563",
  700: "#374151",
  800: "#1f2937",
  900: "#111827",
  950: "#030712",
}

/** Tailwind emerald — success states. */
const success: ShadeScale = {
  50: "#ecfdf5",
  100: "#d1fae5",
  200: "#a7f3d0",
  300: "#6ee7b7",
  400: "#34d399",
  500: "#10b981",
  600: "#059669",
  700: "#047857",
  800: "#065f46",
  900: "#064e3b",
  950: "#022c22",
}

/** Tailwind amber — warning states + chart Creative Concentration/Conversions. */
const warning: ShadeScale = {
  50: "#fffbeb",
  100: "#fef3c7",
  200: "#fde68a",
  300: "#fcd34d",
  400: "#fbbf24",
  500: "#f59e0b",
  600: "#d97706",
  700: "#b45309",
  800: "#92400e",
  900: "#78350f",
  950: "#451a03",
}

/** Tailwind red — error / destructive. */
const error: ShadeScale = {
  50: "#fef2f2",
  100: "#fee2e2",
  200: "#fecaca",
  300: "#fca5a5",
  400: "#f87171",
  500: "#ef4444",
  600: "#dc2626",
  700: "#b91c1c",
  800: "#991b1b",
  900: "#7f1d1d",
  950: "#450a0a",
}

/** Tailwind blue — informational / status-sent badge. */
const info: ShadeScale = {
  50: "#eff6ff",
  100: "#dbeafe",
  200: "#bfdbfe",
  300: "#93c5fd",
  400: "#60a5fa",
  500: "#3b82f6",
  600: "#2563eb",
  700: "#1d4ed8",
  800: "#1e40af",
  900: "#1e3a8a",
  950: "#172554",
}

export const palette = {
  primary,
  secondary,
  neutral,
  error,
  warning,
  success,
  info,
} as const

// ── Extended palette ────────────────────────────────────────

/**
 * AI Mint — DESIGN.md `{colors.ai}` = #00F5C8. Used for AI-generated content,
 * suggestions, enrichment surfaces. Most usages reference opacity-based
 * fills (`aiFillLight`, `aiFillMedium`, `aiFillChip` in themes.ts) rather
 * than solid shades.
 */
const ai: ShadeScale = {
  50: "#effff9",
  100: "#d0fff0",
  200: "#9bfde0",
  300: "#5cf6cf",
  400: "#1ee9bc",
  500: "#00F5C8", // DESIGN.md ai main
  600: "#00997d", // DESIGN.md ai-deep (chart series)
  700: "#0a8c75", // DESIGN.md ai-text
  800: "#0a705f",
  900: "#095a4e",
  950: "#053229",
}

/**
 * Competitor Purple — DESIGN.md `{colors.competitor}` = #6C4BFF.
 * Charts only; never used for interactive UI.
 */
const competitor: ShadeScale = {
  50: "#f1eeff",
  100: "#e2dcff",
  200: "#c5b9ff",
  300: "#a896ff",
  400: "#8b73ff",
  500: "#6C4BFF", // DESIGN.md
  600: "#5638e5",
  700: "#4029b3",
  800: "#2b1c80",
  900: "#160e4d",
  950: "#0a0729",
}

const violet: MinimalShadeScale = generateMinimalScale(extendedSeeds.violet)
const orange: MinimalShadeScale = generateMinimalScale(extendedSeeds.orange)
const lime: MinimalShadeScale = generateMinimalScale(extendedSeeds.lime)

/**
 * Slate chip — DESIGN.md `{colors.slate-chip-bg}` / `{colors.slate-chip-text}`.
 * Background for neutral metadata chips (e.g., script index tags).
 */
const slateChip = {
  bg: "#EEEEF4",
  text: "#464B67",
} as const

/**
 * Categorical chart palette — ordered array is the assignment source of truth.
 * A chart with N unnamed series takes the first N entries. Hues, saturations,
 * and lightnesses are spaced so adjacent series stay distinguishable at 40px
 * bar width (including under deuteranopia / protanopia).
 */
export const CHART_SERIES_PALETTE = [
  "#6BA84F", // 1 — green, mid-dark
  "#7C5CB0", // 2 — purple, dark
  "#F2B33D", // 3 — amber, bright
  "#3D9DA8", // 4 — teal, mid-dark
  "#E86A5C", // 5 — coral, mid
  "#C4629B", // 6 — magenta, mid
  "#4F7DBF", // 7 — blue, mid-dark
  "#A8A03D", // 8 — olive, muted
  "#8B9EC4", // 9 — steel, light (blue-gray; lightness split from chart-7)
  "#D48A3D", // 10 — ochre, mid (brown-yellow; deuteranopia-safe vs amber)
  "#5AAE8A", // 11 — seafoam, mid (cyan-green; blue channel survives protanopia)
  "#B07CC6", // 12 — orchid, mid (lighter than chart-2; hue between purple/magenta)
  "#C75B3A", // 13 — rust, mid-dark (lightness split from coral)
  "#3D7A9E", // 14 — denim, dark (blue-teal; darker than chart-4 and chart-7)
  "#B8A068", // 15 — khaki, muted (warmer/lighter than olive)
  "#7A9B6E", // 16 — sage, muted (gray-green; lighter than chart-1)
  "#9B6BA8", // 17 — violet, mid (between purple and magenta; darker than orchid)
  "#E8C04A", // 18 — sunflower, bright (cooler than amber; warmer than khaki)
  "#2E8B8B", // 19 — dark teal (darker than chart-4; blue-green axis)
  "#D46B7A", // 20 — rose, mid (pinker than coral; not magenta)
  "#6B8FBF", // 21 — periwinkle, mid (lighter than chart-7; not steel)
  "#8B7355", // 22 — taupe, muted (brown family; new hue vs ochre/rust)
  "#4A6B5C", // 23 — forest, dark (dark sage; lightness split from 16)
  "#C4A0B8", // 24 — mauve, light (magenta-gray; lightness split from 17)
] as const

/** Semantic aliases onto {@link CHART_SERIES_PALETTE} for legacy token names. */
const chartPastel = {
  green: CHART_SERIES_PALETTE[0],
  purple: CHART_SERIES_PALETTE[1],
  amber: CHART_SERIES_PALETTE[2],
  teal: CHART_SERIES_PALETTE[3],
  coral: CHART_SERIES_PALETTE[4],
  magenta: CHART_SERIES_PALETTE[5],
  blue: CHART_SERIES_PALETTE[6],
  olive: CHART_SERIES_PALETTE[7],
  // Legacy names — same slots, kept for token back-compat
  yellow: CHART_SERIES_PALETTE[2],
  orange: CHART_SERIES_PALETTE[4],
  red: CHART_SERIES_PALETTE[5],
} as const

/**
 * Chart chrome — axis/tick ink and the horizontal grid rule. Deliberately
 * off the neutral gray ramp: the slate ink stays legible against pastel
 * fills without competing with them for attention.
 */
const chartChrome = {
  axisText: "#4C5773",
  grid: "#EDEDED",
} as const

export const extendedPalette = {
  ai,
  competitor,
  violet,
  orange,
  lime,
  slateChip,
  chartPastel,
  chartChrome,
} as const

export type ExtendedPalette = typeof extendedPalette
